import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";

import { injectChipIcons } from "../chipIcons";
import { Brand, SiteFooter } from "../components/brand";
import { BOOK_CALL_URL } from "../landingData";
import { useBriefTracking } from "../landingClient";
import { canonicalLink, jsonLd, seoMeta, SITE_URL } from "../seo";

const AUDIT_MAILTO = "mailto:hello@throughpilot.com?subject=Free%20audit";

// First two render highlighted (`chip key`), matching the landing hero. Keep
// the list at 6 — a mobile rule hides chips beyond the 6th.
const AUDIT_CHIPS = ["Lovable", "Replit", "Bolt", "Auth", "Payments", "Security"];

export const Route = createFileRoute("/audit")({
  head: () => ({
    meta: seoMeta({
      title: "ThroughPilot - Free Production-Readiness Audit for AI-Built Apps",
      description:
        "A free, hand-done production-readiness audit of your Lovable, Replit, or Bolt build. Every page and button tested on desktop and mobile, findings ranked by severity, effort, and DIY flags - a report that's yours to keep.",
      path: "/audit",
    }),
    links: [canonicalLink("/audit")],
    scripts: [
      jsonLd({
        "@context": "https://schema.org",
        "@type": "Service",
        "@id": `${SITE_URL}/audit#service`,
        url: `${SITE_URL}/audit`,
        name: "Free production-readiness audit for AI-built apps",
        description:
          "Manual production-readiness audit of AI-built products: every page hand-tested on desktop and mobile, findings ranked by severity and effort.",
        provider: { "@id": `${SITE_URL}/#organization` },
        serviceType: "Production-readiness audit",
        areaServed: "Worldwide",
      }),
    ],
  }),
  component: AuditPage,
});

function AuditPage() {
  useBriefTracking(null);

  useEffect(() => {
    injectChipIcons(document);
  }, []);

  return (
    <div className="case-page-body">
      <div className="page-glow" aria-hidden="true" />

      <nav className="case-nav">
        <div className="case-nav-inner">
          <Brand />
          <a className="back" href="/">
            ← throughpilot.com
          </a>
        </div>
      </nav>

      <header className="chero">
        <div className="wrap">
          <div className="kicker">
            <span className="sq" /> Free production-readiness audit
          </div>
          <h1>
            Find out what breaks, <em>before</em> your users do.
          </h1>
          <p className="lede">
            Lovable, Replit, and Bolt get you a working product fast - and
            quietly skip the parts that keep it working. A senior engineer
            goes through your app by hand, page by page, and sends you a
            ranked report of what would break in production. Free, and yours
            to keep.
          </p>
          <div className="meta">
            <div className="m">
              <span className="ml">Format</span>
              <span className="mv">Hand-tested, not a scan</span>
            </div>
            <div className="m">
              <span className="ml">Coverage</span>
              <span className="mv">Every page &amp; button, desktop + mobile</span>
            </div>
            <div className="m">
              <span className="ml">You get</span>
              <span className="mv">Ranked findings report, ~10 pages</span>
            </div>
            <div className="m">
              <span className="ml">Cost</span>
              <span className="mv">Free, no strings</span>
            </div>
          </div>
          <div className="chips">
            {AUDIT_CHIPS.map((chip, index) => (
              <span className={`chip${index < 2 ? " key" : ""}`} key={chip}>
                {chip}
              </span>
            ))}
          </div>
        </div>
      </header>

      {/* HOW IT WORKS */}
      <section>
        <div className="wrap">
          <div className="sec-lbl">How it works</div>
          <h2 className="sec-title">Send a link. Get a straight answer.</h2>
          <div className="stack-grid">
            <div className="scard">
              <div className="t">Step 01</div>
              <div className="v">Send your link</div>
              <div className="d">
                Email the URL of your app with a line on what it does and who
                it's for. That's all it takes to start.
              </div>
            </div>
            <div className="scard">
              <div className="t">Step 02</div>
              <div className="v">It's tested by hand</div>
              <div className="d">
                Every page and every button, on desktop and mobile. Not an
                automated scan - a senior engineer using your product the way
                a real user would. Auth, payments, data, and security get
                extra attention.
              </div>
            </div>
            <div className="scard">
              <div className="t">Step 03</div>
              <div className="v">You get the report</div>
              <div className="d">
                A ranked, plain-language report within a few days: what's
                broken, how bad it is, what a fix takes, and which fixes you
                can do yourself.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THE REPORT */}
      <section>
        <div className="wrap two">
          <div>
            <div className="sec-lbl">The report</div>
            <h2 className="sec-title">Findings you can act on.</h2>
          </div>
          <div>
            <p>
              <b>Ranked by severity.</b> The list starts with whatever can lose
              you users, data, or money - not an alphabetical wall of warnings.
            </p>
            <p>
              <b>Effort on every finding.</b> Each item says roughly what a fix
              involves, so you can plan the work instead of guessing at it.
            </p>
            <p>
              <b>DIY flags.</b> Plenty of findings are things you can fix
              yourself with your AI tool - those are marked, with a hint on how
              to prompt it.
            </p>
            <p>
              <b>Yours to keep.</b> Around ten pages, written in plain
              language. No obligation, no lock-in - take it to any developer
              you like.
            </p>
            <div className="archnote">
              If you'd rather not fix it yourself: after the report there's an
              optional fixed-price fix pass - scoped from the findings, quoted
              up front, taken <b>through</b> to production.
            </div>
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="hood">
        <div className="wrap">
          <div className="sec-lbl">Who it's for</div>
          <h2 className="sec-title">Built with AI, headed for real users.</h2>
          <div className="stack-grid">
            <div className="scard">
              <div className="t">Good fit</div>
              <div className="v">Launching soon</div>
              <div className="d">
                Your build works in the demo, and real users - real logins,
                real payments, real data - are about to touch it.
              </div>
            </div>
            <div className="scard">
              <div className="t">Good fit</div>
              <div className="v">Live but shaky</div>
              <div className="d">
                It's out there, odd bugs keep surfacing, and you want a clear
                picture of what's solid and what isn't.
              </div>
            </div>
            <div className="scard">
              <div className="t">Skip for now</div>
              <div className="v">Still exploring</div>
              <div className="d">
                If you're the only user, keep building. Come back when launch
                is on the calendar - that's when the audit earns its keep.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="contact">
        <div className="contact-inner">
          <div className="sec-kicker" style={{ justifyContent: "center" }}>
            <span className="sq" /> Get your audit
          </div>
          <h2 className="contact-big">
            Know where you <em>stand</em>.
          </h2>
          <p className="contact-sub">
            Email a link to your app - one line about it is enough. No forms,
            no call needed, no obligation. You'll get a reply from the
            engineer who'll run your audit, and the full report within a few
            days.
          </p>
          <div className="contact-ctas">
            <a className="btn pri" href={AUDIT_MAILTO}>
              hello@throughpilot.com <span className="arr">↗</span>
            </a>
            <a
              className="btn ghost"
              href={BOOK_CALL_URL}
              rel="noopener noreferrer"
              target="_blank"
            >
              Book a Call <span className="arr">↗</span>
            </a>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
